import { useEffect, useMemo } from 'react';

import { useSelectFloating } from './useSelectFloating';
import { useSelectFloatingInteractions } from './useSelectFloatingInteractions';

import type { useSelectRootContext } from './SelectRootContext';

type Config = {
    onDropdownVisibleChange: ((open: boolean) => void) | undefined;
    onIsPositionedChange: (positioned: boolean) => void;
    selectRootContextValue: ReturnType<typeof useSelectRootContext>;
};

export const useSelectRootFloating = ({
    onDropdownVisibleChange,
    onIsPositionedChange,
    selectRootContextValue
}: Config) => {
    const {
        isTypeaheadEnabled,
        isTypingRef,
        options,
        selection,
        listHeight,
        setMaxHeight,
        dropdownMatchSelectWidth,
        open,
        setOpen,
        optionFilterProp,
        canDropdownOpen,
        autoComplete
    } = selectRootContextValue;

    const floating = useSelectFloating({
        onDropdownVisibleChange,
        listHeight,
        setMaxHeight,
        dropdownMatchSelectWidth,
        open,
        canDropdownOpen,
        setOpen
    });
    const floatingInteractions = useSelectFloatingInteractions({
        floating,
        options,
        selection,
        isTypeaheadEnabled,
        isTypingRef,
        optionFilterProp,
        focusItemOnOpen: !autoComplete
    });

    useEffect(() => {
        onIsPositionedChange?.(floating.isPositioned);
    }, [floating.isPositioned, onIsPositionedChange]);

    const contextValue = useMemo(
        () => ({
            floatingInteractions,
            floatingNodeId: floating.nodeId,
            floatingRefs: floating.refs,
            focusManagerContext: floating.context,
            computedFloatingStyles: floating.computedFloatingStyles
        }),
        [
            floatingInteractions,
            floating.nodeId,
            floating.refs,
            floating.isPositioned,
            floating.context,
            floating.computedFloatingStyles
        ]
    );

    return contextValue;
};

export type SelectRootFloatingValues = ReturnType<typeof useSelectRootFloating>;
